import { useState, useMemo, useEffect, useCallback } from 'react';
import { Form, Select, DatePicker, Space } from 'antd';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ExcelTable from 'components/ui/excel-table';
import {
  ExcelTableColDef,
  EExcelTablePaginationType,
  ECellValueType,
  EExcelTableColumnType,
  RowData,
} from 'components/ui/excel-table/types';
import usePagination from 'hooks/usePagination';
import PremiumStatusIndicator from 'components/ui/premium-status-indicator';
import { simpleSearch } from 'utils';
import { premiumScheduleApi } from 'services/apis';
import {
  ContractResponseDtoContractStatusEnum,
  GetPremiumDueBodyDtoSortFieldEnum,
  GetPremiumDueBodyDtoSortOrderEnum,
  PremiumDueResponseDtoPremiumStatusEnum,
} from 'libs/pinnacle-sdk';
import { EQueryKeys } from 'constants/query-keys';
import { useNavigate } from 'react-router';
import { constructContractDetailPath, EPageFrom } from 'constants/path';
import FilterButton from 'components/ui/filter-button';
import NocStatusIndicator from 'components/ui/noc-status-indicator';
import { RowClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { SortedSelect } from 'components/ui/sorted-select';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';
import dayjs from 'dayjs';
import { handleError } from 'utils/form';
import PremiumDueTabs from './PremiumDueTabs';
import { EPremiumDueTabs } from './types';

const { RangePicker } = DatePicker;

const PremiumDueTable = () => {
  const navigate = useNavigate();
  const { premiumDueTable: savedFilters, setPremiumDueTableFilters } =
    useDashboardFiltersStore();
  const [activeTab, setActiveTab] = useState<EPremiumDueTabs>(
    EPremiumDueTabs.Unpaid
  );

  const [isFilterOpen, setIsFilterOpen] = useState(savedFilters.isFilterOpen);
  const [form] = Form.useForm();
  const [sortField, setSortField] = useState<
    GetPremiumDueBodyDtoSortFieldEnum | undefined
  >(savedFilters.sortField as GetPremiumDueBodyDtoSortFieldEnum | undefined);
  const [sortOrder, setSortOrder] = useState<
    GetPremiumDueBodyDtoSortOrderEnum | undefined
  >(savedFilters.sortOrder as GetPremiumDueBodyDtoSortOrderEnum | undefined);
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(100);
  const { t } = useTranslation();

  // Restore saved filters to form
  useEffect(() => {
    form.setFieldsValue({
      brokers: savedFilters.selectedBrokers,
      reinsured: savedFilters.selectedReinsureds,
      insured: savedFilters.selectedInsureds,
      binder: savedFilters.selectedBinders,
      premiumStatuses: savedFilters.selectedPremiumStatuses,
      closing: savedFilters.selectedClosing,
      ppwDateRange: savedFilters.selectedPremiumDueDateRange
        ? [
            dayjs(savedFilters.selectedPremiumDueDateRange[0]),
            dayjs(savedFilters.selectedPremiumDueDateRange[1]),
          ]
        : undefined,
    });
  }, []);

  // Watch form values for filtering
  const selectedBrokers = Form.useWatch('brokers', form);
  const selectedReinsured = Form.useWatch('reinsured', form);
  const selectedInsured = Form.useWatch('insured', form);
  const selectedBinder = Form.useWatch('binder', form);
  const selectedPremiumStatuses = Form.useWatch('premiumStatuses', form);
  const selectedClosing = Form.useWatch('closing', form);
  const ppwDateRange = Form.useWatch('ppwDateRange', form);

  // Save filters to store whenever they change
  useEffect(() => {
    setPremiumDueTableFilters({
      selectedBrokers,
      selectedReinsureds: selectedReinsured,
      selectedInsureds: selectedInsured,
      selectedBinders: selectedBinder,
      selectedPremiumStatuses,
      selectedClosing,
      selectedPremiumDueDateRange: ppwDateRange
        ? [ppwDateRange[0]?.toDate(), ppwDateRange[1]?.toDate()]
        : undefined,
      sortField: sortField as string | undefined,
      sortOrder: sortOrder as string | undefined,
      isFilterOpen,
    });
  }, [
    selectedBrokers,
    selectedReinsured,
    selectedInsured,
    selectedBinder,
    selectedPremiumStatuses,
    selectedClosing,
    ppwDateRange,
    sortField,
    sortOrder,
    isFilterOpen,
    setPremiumDueTableFilters,
  ]);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [
    selectedBrokers,
    selectedReinsured,
    selectedInsured,
    selectedBinder,
    selectedPremiumStatuses,
    selectedClosing,
    ppwDateRange,
    sortField,
    sortOrder,
    resetPagination,
  ]);

  const { data: options } = useQuery(
    [EQueryKeys.PREMIUM_DUE_OPTIONS],
    () => premiumScheduleApi.getPremiumDueOptions(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Extract unique values for filter options
  const brokerOptions = useMemo(
    () =>
      options?.brokers.map((broker) => ({
        label: broker.name,
        value: broker.brokerId,
      })),
    [options]
  );

  const reinsuredOptions = useMemo(
    () =>
      options?.reinsured.map((reinsured) => ({
        label: reinsured.name,
        value: reinsured.reinsuredId,
      })),
    [options]
  );

  const binderOptions = useMemo(
    () =>
      options?.binder.map((binder) => ({
        label: binder.name,
        value: binder.binderId,
      })),
    [options]
  );

  const insuredOptions = useMemo(
    () =>
      options?.insured.map((insured) => ({
        label: insured.name,
        value: insured.insuredId,
      })),
    [options]
  );

  const premiumStatusOptions = useMemo(
    () => [
      {
        label: (
          <PremiumStatusIndicator
            status={PremiumDueResponseDtoPremiumStatusEnum.Current}
          />
        ),
        value: PremiumDueResponseDtoPremiumStatusEnum.Current,
      },
      {
        label: (
          <PremiumStatusIndicator
            status={PremiumDueResponseDtoPremiumStatusEnum.Overdue30Days}
          />
        ),
        value: PremiumDueResponseDtoPremiumStatusEnum.Overdue30Days,
      },
      {
        label: (
          <PremiumStatusIndicator
            status={PremiumDueResponseDtoPremiumStatusEnum.Overdue60Days}
          />
        ),
        value: PremiumDueResponseDtoPremiumStatusEnum.Overdue60Days,
      },
      {
        label: (
          <PremiumStatusIndicator
            status={PremiumDueResponseDtoPremiumStatusEnum.Overdue90Days}
          />
        ),
        value: PremiumDueResponseDtoPremiumStatusEnum.Overdue90Days,
      },
      {
        label: (
          <PremiumStatusIndicator
            status={
              PremiumDueResponseDtoPremiumStatusEnum.OverdueMoreThan90Days
            }
          />
        ),
        value: PremiumDueResponseDtoPremiumStatusEnum.OverdueMoreThan90Days,
      },
    ],
    []
  );

  const {
    data: { items: premiumDueData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading: isLoadingPremiumDue,
  } = useQuery(
    [
      EQueryKeys.PREMIUM_DUE,
      pageSize,
      currentPage,
      selectedBrokers,
      selectedReinsured,
      selectedInsured,
      selectedBinder,
      selectedPremiumStatuses,
      selectedClosing,
      ppwDateRange,
      sortField,
      sortOrder,
      activeTab,
    ],
    async () => {
      const { items, pagination } = await premiumScheduleApi.filterPremiumDue({
        getPremiumDueBodyDto: {
          page: currentPage,
          pageSize,
          sortField,
          sortOrder,
          brokerIds: selectedBrokers,
          reinsuredIds: selectedReinsured,
          insuredIds: selectedInsured,
          binderIds: selectedBinder,
          ppwDueDateFrom: ppwDateRange?.[0]?.startOf('day').toDate(),
          ppwDueDateTo: ppwDateRange?.[1]?.endOf('day').toDate(),
          premiumStatus:
            activeTab === EPremiumDueTabs.Unpaid
              ? selectedPremiumStatuses
              : undefined,
          closing: selectedClosing,
          isPaid: activeTab === EPremiumDueTabs.Paid,
        },
      });
      return {
        items: items.map((item, index) => ({
          ...item,
          index: pageSize * (currentPage - 1) + index,
        })),
        pagination,
      };
    }
  );

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    const sortedCol = event.api.getColumnState().find((col) => col.sort);
    if (sortedCol) {
      setSortField(sortedCol.colId as GetPremiumDueBodyDtoSortFieldEnum);
      setSortOrder(
        sortedCol.sort === 'asc'
          ? GetPremiumDueBodyDtoSortOrderEnum.Asc
          : GetPremiumDueBodyDtoSortOrderEnum.Desc
      );
    } else {
      setSortField(undefined);
      setSortOrder(undefined);
    }
  }, []);

  const updateClosingStatusMutation = useMutation(
    async ({
      premiumScheduleId,
      closing,
    }: {
      premiumScheduleId: string;
      closing: boolean;
    }) => {
      return premiumScheduleApi.updatePremiumSchedule({
        premiumScheduleId,
        updatePremiumSchedulePayloadDto: {
          closing,
        },
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('premiumDue.updateClosingStatusFail'),
        });
      },
    }
  );

  const onDataChange = useCallback(
    async (
      affectedRow: RowData<{ closing?: boolean; premiumScheduleId: string }>
    ) => {
      await updateClosingStatusMutation.mutateAsync({
        premiumScheduleId: affectedRow.data.premiumScheduleId,
        closing: affectedRow.data.closing ?? false,
      });
    },
    [updateClosingStatusMutation]
  );

  const columns: ExcelTableColDef[] = useMemo(
    () => [
      {
        colId: GetPremiumDueBodyDtoSortFieldEnum.Broker,
        field: 'broker',
        headerName: t('broker.broker'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
      },
      {
        colId: GetPremiumDueBodyDtoSortFieldEnum.Reinsured,
        field: 'reinsured',
        headerName: t('reinsured.reinsured'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
      },
      {
        colId: GetPremiumDueBodyDtoSortFieldEnum.Insured,
        field: 'insured',
        headerName: t('insured.insured'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
      },
      {
        field: 'inceptionDate',
        headerName: t('submission.inceptionDate'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
      {
        field: 'expiryDate',
        headerName: t('submission.expiryDate'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
      {
        field: 'binder',
        headerName: t('binder.binder'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
      },
      {
        field: 'pinnacleRefNo',
        headerName: t('contractDetail.documentNumber'),
        minWidth: 200,
        flex: 1,
        editable: false,
        resizable: true,
        textAlign: 'left',
      },
      {
        field: 'description',
        headerName: t('premiumDue.description'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        field: 'currency',
        headerName: t('general.currency'),
        minWidth: 100,
        flex: 1,
        editable: false,
        resizable: true,
        textAlign: 'center',
      },
      {
        field: 'netAmountDueFromBroker',
        headerName: t('premiumDue.netPremiumDueFromBroker'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        textAlign: 'right',
        precision: 2,
      },
      {
        headerName: t('premiumDue.closing'),
        field: 'closing',
        width: 100,
        editable: true,
        resizable: false,
        columnType: EExcelTableColumnType.CHECKBOX,
      },
      {
        colId: GetPremiumDueBodyDtoSortFieldEnum.PremiumStatus,
        field: 'premiumStatus',
        headerName: t('premiumDue.premiumStatus'),
        minWidth: 200,
        flex: 1,
        editable: false,
        resizable: true,
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
        cellRenderer: (params: any) => (
          <PremiumStatusIndicator status={params.value} />
        ),
      },
      {
        colId: GetPremiumDueBodyDtoSortFieldEnum.PpwDueDate,
        field: 'ppwDueDate',
        headerName: t('premiumDue.ppwDueDate'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        textAlign: 'center',
        sortable: true,
        unSortIcon: true,
        comparator: () => 0,
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
      {
        field: 'nocStatus',
        headerName: t('premiumDue.noticeOfCancellationStatus'),
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        textAlign: 'center',
        cellRenderer: (params: any) =>
          params.value ? (
            <div className="flex justify-center">
              <NocStatusIndicator status={params.value} />
            </div>
          ) : null,
      },
      {
        field: 'contractId',
        headerName: '',
        width: 0,
        hide: true,
        editable: false,
      },
    ],
    [t]
  );

  const handleRowClick = (row: RowClickedEvent) => {
    const cellElement = (row.event?.target as HTMLElement)?.closest('[col-id]');
    if (cellElement?.getAttribute('col-id') === 'closing') {
      return;
    }
    navigate(constructContractDetailPath(row.data.contractId, '1'), {
      state: {
        from: EPageFrom.FINANCE_DASHBOARD,
      },
    });
  };

  return (
    <div className="w-full">
      <Form form={form} className="w-full">
        <div className="mb-4 flex items-center gap-2 justify-between">
          <div className="text-2xl font-medium">{t('premiumDue.title')}</div>
          <FilterButton
            isActive={isFilterOpen}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          />
        </div>

        {isFilterOpen && (
          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
            <Space direction="horizontal" wrap className="w-full">
              <Form.Item name="brokers" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('premiumDue.selectBrokers')}
                  options={brokerOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="reinsured" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('premiumDue.selectReinsured')}
                  options={reinsuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    simpleSearch((option?.label ?? '') as string, input)
                  }
                />
              </Form.Item>

              <Form.Item name="insured" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('premiumDue.selectInsured')}
                  options={insuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="binder" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('binder.selectBinder')}
                  options={binderOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {activeTab === EPremiumDueTabs.Unpaid && (
                <Form.Item
                  name="premiumStatuses"
                  className="min-w-[250px] mb-2"
                >
                  <SortedSelect
                    mode="multiple"
                    placeholder={t('premiumDue.selectPremiumStatus')}
                    options={premiumStatusOptions}
                    size="large"
                    maxTagCount={2}
                    className="rounded-xl"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      ((option?.value ?? '') as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item name="closing" className="min-w-[150px] mb-2">
                <Select
                  placeholder={t('premiumDue.selectClosing')}
                  options={[
                    { label: t('general.yes'), value: true },
                    { label: t('general.no'), value: false },
                  ]}
                  size="large"
                  className="rounded-xl"
                  allowClear
                />
              </Form.Item>

              <Form.Item name="ppwDateRange" className="w-[280px] mb-2">
                <RangePicker
                  format="DD/MM/YYYY"
                  size="large"
                  placeholder={[
                    t('premiumDue.ppwDateFrom'),
                    t('premiumDue.ppwDateTo'),
                  ]}
                  allowClear
                  className="w-full"
                />
              </Form.Item>
            </Space>
          </div>
        )}
      </Form>

      <div className="flex justify-between items-center">
        <PremiumDueTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <ExcelTable
        data={premiumDueData || []}
        columnDefs={columns}
        onRowClicked={handleRowClick}
        paginationType={EExcelTablePaginationType.DETAIL}
        currentPage={currentPage}
        onDataChange={onDataChange}
        pageSize={pageSize}
        totalRows={total || 0}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        loading={isLoadingPremiumDue}
        tableContainerClassName="h-[28rem]"
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        onSortChanged={handleSortChanged}
      />
    </div>
  );
};

export default PremiumDueTable;
